import { Action, AsyncThunkAction, Middleware, StoreEnhancer, ThunkAction, combineReducers, configureStore } from '@reduxjs/toolkit'
import type { StorageAdapter } from '../platform/storage'
import { persistStore, persistReducer, PersistConfig } from 'redux-persist'

import membershipReducer, { redactMembership } from './membershipSlice'
import dependentsReducer, { redactDependents } from './dependentsSlice'
import expensesReducer, { isExpensesSliceAction, redactExpenses, redactExpensesSliceAction } from './expensesSlice'
import incidentsReducer, { isIncidentsSliceAction, redactIncidents, redactIncidentsSliceAction } from './incidentsSlice'
import attachmentsReducer, { redactAttachments, isAttachmentsSliceAction, redactAttachmentsSliceAction } from './attachmentsSlice'
import submissionsReducer, { isSubmissionsSliceAction, redactSubmissions, redactSubmissionsSliceAction } from './submissionsSlice'
import syncReducer, { isSyncSliceAction, redactSyncSlice, redactSyncSliceAction } from './syncSlice'
import advancesReducer, { redactAdvances, isAdvancesSliceAction, redactAdvancesSliceAction } from './advancesSlice'
import { DeepPartialNullable } from '../types/util';
import { incidentStartDateMiddleware } from './middleware/incidentStartDateMiddleware';
import { reopenClosedIncidentMiddleware } from './middleware/reopenClosedIncidentMiddleware';
import notificationsReducer, { redactNotifications, isNotificationsSliceAction, redactNotificationsSliceAction } from './notificationsSlice'
import notificationHidesReducer from './notificationHidesSlice'
import providersReducer, { redactProviders, isProvidersSliceAction, redactProvidersSliceAction } from './providersSlice'
import todosReducer, { redactTodos, isTodosSliceAction, redactTodosSliceAction } from './todosSlice'
import blobsReducer, { redactBlobs, isBlobsSliceAction, redactBlobsSliceAction } from './blobsSlice'
import conversationsReducer, { redactConversations, isConversationsSliceAction, redactConversationsSliceAction } from './conversationsSlice'
import plaidReducer, { redactPlaid } from './plaidSlice'

/**
 * Exported so a test or a Storybook decorator can build an unpersisted store
 * over exactly the same reducer map `createStore` uses. Re-listing the slices
 * at the call site is how the two drift when a slice is added.
 */
export const rootReducer = combineReducers({
  membership: membershipReducer,
  dependents: dependentsReducer,
  expenses: expensesReducer,
  incidents: incidentsReducer,
  attachments: attachmentsReducer,
  submissions: submissionsReducer,
  advances: advancesReducer,
  notifications: notificationsReducer,
  notificationHides: notificationHidesReducer,
  sync: syncReducer,
  providers: providersReducer,
  todos: todosReducer,
  blobs: blobsReducer,
  conversations: conversationsReducer,
  plaid: plaidReducer,
})

export type RootState = ReturnType<typeof rootReducer>

/**
 * Builds the persisted root store.
 *
 * Parameterised so the Expo app and the embeddable SDK can build the same store
 * with their own persistence engine, Redux enhancers, and middleware list, rather
 * than inheriting localStorage and @sentry/react. See
 * docs/plans/embeddable-sdk.md.
 *
 * The web store and the SDK store are now composed the same way. They used to
 * differ: the web app generated todos optimistically via
 * todoReconciliationMiddleware, which the SDK could not ship, and composing that
 * middleware without the generators would have completed-or-deleted every todo
 * synced down from the backend. Todos are now generated only on the backend, so
 * neither the middleware nor that hazard exists.
 */
export function createStore({
  storage: storageEngine,
  middleware = [incidentStartDateMiddleware, reopenClosedIncidentMiddleware],
  enhancers = [],
  // Bumped from `root/2023-04-29` because the sync endpoint spent a release handing
  // `jsonb` and `text[]` columns down as the raw strings Postgres printed rather than as
  // structured data (fixed in SyncReader::presentRow). Every client that synced during
  // that window is holding a todo whose `action` is a JSON string, an `expense_ids` that
  // is `"{a,b}"`, a `cpt_codes` with no `.some()`, and so on -- and the sync engine will
  // not re-send those rows until they change again, so nothing heals them on its own.
  // Discarding the lot and re-downloading is the cheap fix.
  persistKey = `root/2026-08-26`,
}: {
  storage: StorageAdapter,
  middleware?: Middleware<{}, RootState>[],
  enhancers?: StoreEnhancer[],
  // Doubles as the persisted-state schema version: changing it discards any
  // previously persisted state, including un-synced updates. Bumping it costs
  // every client its `sync.pending` queue, so it is a last resort for state the
  // sync engine cannot repair itself -- not a routine migration tool.
  persistKey?: string,
}) {
  const persistConfig: PersistConfig<RootState> = {
    key: persistKey,
    storage: storageEngine as PersistConfig<RootState>['storage'],
    version: 1,
  }

  const persistedReducer = persistReducer(persistConfig, rootReducer)

  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false // redux-persist doesn't like this
      })
      .concat(middleware),
    enhancers,
  })

  return { store, persistor: persistStore(store) }
}

// TODO: refine the available action types here
export type AppAction = Action<any> | AsyncThunkAction<any, any, any> | ThunkAction<any, any, any, any>
/**
 * Computed from `createStore`'s return type rather than from a concrete store
 * instance -- this module builds no store of its own. The web app's concrete
 * instance (with its Sentry enhancer and localStorage engine) lives in
 * `apps/web/src/app/reduxToolkit/store.ts`; the SDK and a native app build
 * their own the same way. See docs/plans/embeddable-sdk.md.
 */
export type AppDispatch = ReturnType<typeof createStore>['store']['dispatch']

/**
 * Redacts the state to remove sensitive information.
 * Due to HIPAA compliance, we cannot send any patient information to analytics tools like Sentry.
 */
export function redactState(state: RootState): DeepPartialNullable<RootState> {
  // Transform the state to remove sensitive information
  // Use a whitelist model
  return {
    dependents: redactDependents(state.dependents),
    expenses: redactExpenses(state.expenses),
    incidents: redactIncidents(state.incidents),
    attachments: redactAttachments(state.attachments),
    membership: redactMembership(state.membership),
    submissions: redactSubmissions(state.submissions),
    advances: redactAdvances(state.advances),
    sync: redactSyncSlice(state.sync),
    notifications: redactNotifications(state.notifications),
    notificationHides: { byId: {} },
    providers: redactProviders(state.providers),
    todos: redactTodos(state.todos),
    blobs: redactBlobs(state.blobs),
    conversations: redactConversations(state.conversations),
    plaid: redactPlaid(state.plaid),
  }
}
